import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Plus, Trash2, AlertTriangle, AlertCircle, ExternalLink, X, Pencil, LifeBuoy, Flame, Info } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SAFETY_AGENCY_LINKS } from "@/lib/blogPosts";

/**
 * SafetyView — vessel-scoped safety compliance tracker.
 *
 * Kinds:
 *   flares            → expire 42 months from `replaced_date`
 *   fire_extinguisher → expire 12 years from `manufacture_date`
 *   pfd               → no expiry (audit-only)
 *
 * Backend endpoints (routes/safety.py):
 *   GET    /api/vessels/:id/safety-items
 *   POST   /api/vessels/:id/safety-items
 *   PATCH  /api/vessels/:id/safety-items/:itemId
 *   DELETE /api/vessels/:id/safety-items/:itemId
 *
 * The parent Dashboard also fetches this list to surface the top-of-page
 * red/amber compliance banner — mounts of this component don't own that
 * banner, they only own the tab UI.
 */
export default function SafetyView({ vesselId, canWrite = true, onItemsChanged }) {
    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");
    const [editorKind, setEditorKind] = useState(null); // "flares" | "fire_extinguisher" | "pfd"
    const [editorItem, setEditorItem] = useState(null); // existing item = edit

    const load = async () => {
        setErr("");
        try {
            const { data } = await api.get(`/vessels/${vesselId}/safety-items`);
            setItems(data || []);
        } catch (e) {
            setErr(formatApiErrorDetail(e));
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [vesselId]);

    const bucketed = useMemo(() => {
        const b = { flares: [], fire_extinguisher: [], pfd: [] };
        for (const it of (items || [])) {
            if (b[it.kind]) b[it.kind].push(it);
        }
        return b;
    }, [items]);

    const openAdd = (kind) => {
        setEditorItem(null);
        setEditorKind(kind);
    };
    const openEdit = (item) => {
        setEditorItem(item);
        setEditorKind(item.kind);
    };
    const onSaved = async () => {
        setEditorItem(null);
        setEditorKind(null);
        await load();
        if (onItemsChanged) onItemsChanged();
    };
    const onDelete = async (item) => {
        if (!window.confirm(`Delete "${item.label}"?`)) return;
        try {
            await api.delete(`/vessels/${vesselId}/safety-items/${item.id}`);
            await load();
            if (onItemsChanged) onItemsChanged();
        } catch (e) {
            setErr(formatApiErrorDetail(e));
        }
    };

    if (items === null) {
        return <div data-testid="safety-loading" style={{ padding: 20, color: "var(--bm-text-muted)" }}>Loading safety items…</div>;
    }

    return (
        <div data-testid="safety-view">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 24 }}>
                <div>
                    <h2 className="bm-serif" style={{ fontSize: 32, margin: 0, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 10 }}>
                        <ShieldCheck size={26} color="var(--bm-gold)" /> Safety Compliance
                    </h2>
                    <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 0", fontSize: 14, maxWidth: 640 }}>
                        Track your flares, fire extinguishers and PFDs. We&apos;ll surface red / amber alerts on the dashboard 30 days before anything expires.
                    </p>
                </div>
            </div>

            {err && <div data-testid="safety-error" style={{ color: "var(--bm-bad)", marginBottom: 14 }}>{err}</div>}

            <SafetyGroup
                testid="safety-group-flares"
                title="Flares"
                icon={<Flame size={18} color="var(--bm-warn)" />}
                subtitle="Pyrotechnic distress signals — expire 42 months from replacement date (USCG §160.028)."
                kind="flares"
                items={bucketed.flares}
                canWrite={canWrite}
                onAdd={() => openAdd("flares")}
                onEdit={openEdit}
                onDelete={onDelete}
            />

            <SafetyGroup
                testid="safety-group-extinguishers"
                title="Fire Extinguishers"
                icon={<Flame size={18} color="var(--bm-bad)" />}
                subtitle="Marine fire extinguishers manufactured after 2017 expire 12 years from the date on the label."
                kind="fire_extinguisher"
                items={bucketed.fire_extinguisher}
                canWrite={canWrite}
                onAdd={() => openAdd("fire_extinguisher")}
                onEdit={openEdit}
                onDelete={onDelete}
            />

            <SafetyGroup
                testid="safety-group-pfds"
                title="Personal Flotation Devices (PFDs)"
                icon={<LifeBuoy size={18} color="var(--bm-good)" />}
                subtitle="PFDs don&apos;t expire, but log type + quantity so you're audit-ready during a safety check."
                kind="pfd"
                items={bucketed.pfd}
                canWrite={canWrite}
                onAdd={() => openAdd("pfd")}
                onEdit={openEdit}
                onDelete={onDelete}
            />

            <USCGRequirementsGuide />
            <InternationalAgencyLinks />

            {editorKind && (
                <SafetyItemEditor
                    vesselId={vesselId}
                    kind={editorKind}
                    initial={editorItem}
                    onClose={() => { setEditorItem(null); setEditorKind(null); }}
                    onSaved={onSaved}
                />
            )}
        </div>
    );
}

// ---------------- Sub-components ----------------
function StatusPill({ status, days }) {
    if (status === "red") {
        return (
            <span data-testid="safety-status-red" className="bm-tag" style={{ background: "rgba(255, 89, 89, 0.15)", borderColor: "rgba(255, 89, 89, 0.5)", color: "var(--bm-bad)", fontSize: 11, padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <AlertCircle size={11} /> Overdue {days != null ? `by ${Math.abs(days)}d` : ""}
            </span>
        );
    }
    if (status === "amber") {
        return (
            <span data-testid="safety-status-amber" className="bm-tag bm-tag-warn" style={{ background: "rgba(251, 191, 36, 0.12)", borderColor: "rgba(251, 191, 36, 0.5)", fontSize: 11, padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <AlertTriangle size={11} /> Expires in {days}d
            </span>
        );
    }
    if (status === "green") {
        return (
            <span data-testid="safety-status-green" className="bm-tag bm-tag-good" style={{ fontSize: 11, padding: "3px 8px" }}>
                OK
            </span>
        );
    }
    return null;
}

function SafetyGroup({ testid, title, icon, subtitle, kind, items, canWrite, onAdd, onEdit, onDelete }) {
    return (
        <section data-testid={testid} className="bm-card" style={{ padding: 18, marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <div style={{ minWidth: 240 }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {icon} {title}
                    </h3>
                    <p style={{ margin: "6px 0 0", color: "var(--bm-text-muted)", fontSize: 13, maxWidth: 640, lineHeight: 1.55 }}>
                        {subtitle}
                    </p>
                </div>
                {canWrite && (
                    <button
                        data-testid={`safety-add-${kind}-btn`}
                        className="bm-btn bm-btn-ghost"
                        onClick={onAdd}
                        style={{ padding: "6px 12px", fontSize: 13 }}
                    >
                        <Plus size={14} /> Add
                    </button>
                )}
            </div>

            {items.length === 0 && (
                <div data-testid={`safety-empty-${kind}`} style={{ padding: "14px 0", color: "var(--bm-text-dim)", fontSize: 13, fontStyle: "italic" }}>
                    No {title.toLowerCase()} logged yet.
                </div>
            )}

            {items.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                    <table className="bm-table" style={{ width: "100%" }}>
                        <thead>
                            <tr>
                                <th>Label</th>
                                {kind === "flares" && <th>Replaced</th>}
                                {kind === "fire_extinguisher" && <><th>Manufactured</th><th>Last inspection</th></>}
                                {kind === "pfd" && <><th>Type</th><th>Qty</th></>}
                                {kind !== "pfd" && <th>Expires</th>}
                                <th>Location</th>
                                <th>Status</th>
                                <th style={{ width: 100 }}>{/* actions */}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} data-testid={`safety-row-${it.id}`}>
                                    <td>
                                        {it.label}
                                        {it.notes && <div style={{ fontSize: 11, color: "var(--bm-text-muted)", marginTop: 2 }}>{it.notes}</div>}
                                    </td>
                                    {kind === "flares" && <td style={{ fontVariantNumeric: "tabular-nums" }}>{it.replaced_date || "—"}</td>}
                                    {kind === "fire_extinguisher" && (
                                        <>
                                            <td style={{ fontVariantNumeric: "tabular-nums" }}>{it.manufacture_date || "—"}</td>
                                            <td style={{ fontVariantNumeric: "tabular-nums" }}>{it.inspection_date || "—"}</td>
                                        </>
                                    )}
                                    {kind === "pfd" && (
                                        <>
                                            <td>{it.pfd_type ? `Type ${it.pfd_type}` : "—"}</td>
                                            <td style={{ fontVariantNumeric: "tabular-nums" }}>{it.quantity ?? "—"}</td>
                                        </>
                                    )}
                                    {kind !== "pfd" && (
                                        <td style={{ fontVariantNumeric: "tabular-nums", color: it.status === "red" ? "var(--bm-bad)" : it.status === "amber" ? "var(--bm-warn)" : undefined }}>
                                            {it.expiry_date || "—"}
                                        </td>
                                    )}
                                    <td style={{ color: "var(--bm-text-muted)" }}>{it.location || "—"}</td>
                                    <td><StatusPill status={it.status} days={it.days_until_expiry} /></td>
                                    <td>
                                        {canWrite && (
                                            <div style={{ display: "flex", gap: 6 }}>
                                                <button
                                                    data-testid={`safety-edit-${it.id}`}
                                                    className="bm-icon-btn"
                                                    onClick={() => onEdit(it)}
                                                    title="Edit"
                                                    style={{ background: "transparent", border: "1px solid var(--bm-border-soft)", padding: 4, borderRadius: 4, cursor: "pointer", color: "var(--bm-text-muted)" }}
                                                >
                                                    <Pencil size={13} />
                                                </button>
                                                <button
                                                    data-testid={`safety-delete-${it.id}`}
                                                    className="bm-icon-btn"
                                                    onClick={() => onDelete(it)}
                                                    title="Delete"
                                                    style={{ background: "transparent", border: "1px solid var(--bm-border-soft)", padding: 4, borderRadius: 4, cursor: "pointer", color: "var(--bm-text-muted)" }}
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

function SafetyItemEditor({ vesselId, kind, initial, onClose, onSaved }) {
    const isEdit = !!initial;
    const [form, setForm] = useState(() => ({
        label: initial?.label || defaultLabel(kind),
        location: initial?.location || "",
        replaced_date: initial?.replaced_date || "",
        manufacture_date: initial?.manufacture_date || "",
        inspection_date: initial?.inspection_date || "",
        pfd_type: initial?.pfd_type || "",
        quantity: initial?.quantity ?? "",
        notes: initial?.notes || "",
    }));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const save = async () => {
        if (!form.label.trim()) {
            setErr("Label is required.");
            return;
        }
        setBusy(true);
        setErr("");
        const payload = {
            kind,
            label: form.label.trim(),
            location: form.location.trim() || null,
            replaced_date: form.replaced_date || null,
            manufacture_date: form.manufacture_date || null,
            inspection_date: form.inspection_date || null,
            pfd_type: form.pfd_type || null,
            quantity: form.quantity === "" ? null : Number(form.quantity),
            notes: form.notes.trim() || null,
        };
        try {
            if (isEdit) {
                await api.patch(`/vessels/${vesselId}/safety-items/${initial.id}`, payload);
            } else {
                await api.post(`/vessels/${vesselId}/safety-items`, payload);
            }
            onSaved();
        } catch (e) {
            setErr(formatApiErrorDetail(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="safety-editor-modal"
            role="dialog"
            aria-modal="true"
            style={{
                position: "fixed", inset: 0, background: "rgba(4, 12, 22, 0.72)",
                zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bm-card" style={{ maxWidth: 480, width: "100%", padding: 22, maxHeight: "90vh", overflowY: "auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                        {isEdit ? "Edit" : "Add"} {kindLabel(kind)}
                    </h3>
                    <button data-testid="safety-editor-close" onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--bm-text-muted)", cursor: "pointer" }}>
                        <X size={18} />
                    </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <Field label="Label">
                        <input
                            data-testid="safety-editor-label"
                            className="bm-input"
                            value={form.label}
                            onChange={(e) => set("label", e.target.value)}
                            placeholder={defaultLabel(kind)}
                        />
                    </Field>

                    <Field label="Location on vessel">
                        <input
                            data-testid="safety-editor-location"
                            className="bm-input"
                            value={form.location}
                            onChange={(e) => set("location", e.target.value)}
                            placeholder="e.g. helm locker, saloon"
                        />
                    </Field>

                    {kind === "flares" && (
                        <Field label="Replaced date" hint="Expiry = replaced date + 42 months.">
                            <input
                                data-testid="safety-editor-replaced"
                                type="date"
                                className="bm-input"
                                value={form.replaced_date}
                                onChange={(e) => set("replaced_date", e.target.value)}
                            />
                        </Field>
                    )}

                    {kind === "fire_extinguisher" && (
                        <>
                            <Field label="Manufacture date" hint="Expiry = manufacture date + 12 years (USCG post-2017 rule).">
                                <input
                                    data-testid="safety-editor-manufacture"
                                    type="date"
                                    className="bm-input"
                                    value={form.manufacture_date}
                                    onChange={(e) => set("manufacture_date", e.target.value)}
                                />
                            </Field>
                            <Field label="Last inspection date" hint="Annual monthly visual check + weight check.">
                                <input
                                    data-testid="safety-editor-inspection"
                                    type="date"
                                    className="bm-input"
                                    value={form.inspection_date}
                                    onChange={(e) => set("inspection_date", e.target.value)}
                                />
                            </Field>
                        </>
                    )}

                    {kind === "pfd" && (
                        <>
                            <Field label="Type" hint="Type I, II, III or V per USCG classification.">
                                <select
                                    data-testid="safety-editor-pfd-type"
                                    className="bm-input"
                                    value={form.pfd_type}
                                    onChange={(e) => set("pfd_type", e.target.value)}
                                >
                                    <option value="">Select…</option>
                                    <option value="I">Type I — Offshore</option>
                                    <option value="II">Type II — Near-shore</option>
                                    <option value="III">Type III — Flotation aid</option>
                                    <option value="IV">Type IV — Throwable</option>
                                    <option value="V">Type V — Special use</option>
                                </select>
                            </Field>
                            <Field label="Quantity">
                                <input
                                    data-testid="safety-editor-quantity"
                                    type="number"
                                    min={0}
                                    max={100}
                                    className="bm-input"
                                    value={form.quantity}
                                    onChange={(e) => set("quantity", e.target.value)}
                                    placeholder="e.g. 4"
                                />
                            </Field>
                        </>
                    )}

                    <Field label="Notes">
                        <textarea
                            data-testid="safety-editor-notes"
                            className="bm-input"
                            value={form.notes}
                            onChange={(e) => set("notes", e.target.value)}
                            rows={2}
                            placeholder="Optional notes"
                        />
                    </Field>

                    {err && <div style={{ color: "var(--bm-bad)", fontSize: 13 }}>{err}</div>}

                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
                        <button className="bm-btn bm-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
                        <button
                            data-testid="safety-editor-save"
                            className="bm-btn bm-btn-gold"
                            onClick={save}
                            disabled={busy}
                        >
                            {busy ? "Saving…" : isEdit ? "Save changes" : "Add item"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <div style={{ fontSize: 12, color: "var(--bm-text-muted)", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                {label}
                {hint && (
                    <span title={hint} aria-label={hint} style={{ display: "inline-flex", cursor: "help", opacity: 0.7 }}>
                        <Info size={11} />
                    </span>
                )}
            </div>
            {children}
        </div>
    );
}

function USCGRequirementsGuide() {
    // USCG minimum required safety equipment by vessel length. Sourced from
    // 46 CFR §25 + the USCG Boating Safety Recreational Equipment guide.
    const rows = [
        { size: "Under 16 ft (< 4.9m)", pfds: "1 wearable per person", throwable: "Not required", extinguishers: "1 B-I (if enclosed engine or fuel)", flares: "Not required (except night ops)" },
        { size: "16–26 ft (4.9–7.9m)", pfds: "1 wearable per person", throwable: "1 Type IV throwable", extinguishers: "1 B-I (if enclosed engine or fuel)", flares: "3 day + 3 night (or 3 day/night)" },
        { size: "26–40 ft (7.9–12.2m)", pfds: "1 wearable per person", throwable: "1 Type IV throwable", extinguishers: "2 B-I OR 1 B-II", flares: "3 day + 3 night (or 3 day/night)" },
        { size: "40–65 ft (12.2–19.8m)", pfds: "1 wearable per person", throwable: "1 Type IV throwable", extinguishers: "3 B-I OR 1 B-I + 1 B-II", flares: "3 day + 3 night (or 3 day/night)" },
    ];
    return (
        <section data-testid="safety-uscg-guide" className="bm-card" style={{ padding: 18, marginBottom: 18 }}>
            <div style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>USCG Minimum Required Equipment</h3>
                <p style={{ margin: "6px 0 0", color: "var(--bm-text-muted)", fontSize: 13 }}>
                    U.S. recreational boats — minimums by vessel length. State laws may add on top.
                </p>
            </div>
            <div style={{ overflowX: "auto" }}>
                <table className="bm-table" style={{ width: "100%", fontSize: 13 }}>
                    <thead>
                        <tr>
                            <th>Vessel length</th>
                            <th>PFDs</th>
                            <th>Throwable</th>
                            <th>Fire extinguishers</th>
                            <th>Visual distress signals</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.size}>
                                <td style={{ fontWeight: 600 }}>{r.size}</td>
                                <td>{r.pfds}</td>
                                <td>{r.throwable}</td>
                                <td>{r.extinguishers}</td>
                                <td>{r.flares}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: "var(--bm-text-dim)", lineHeight: 1.55 }}>
                Also required regardless of length: a working sound-producing device (whistle / horn), navigation lights for night operation, and — federal MSD rules — a marine-sanitation device on any vessel with an installed toilet.
            </div>
        </section>
    );
}

function InternationalAgencyLinks() {
    return (
        <section data-testid="safety-agency-links" className="bm-card" style={{ padding: 18, marginBottom: 18 }}>
            <div style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>International Safety References</h3>
                <p style={{ margin: "6px 0 0", color: "var(--bm-text-muted)", fontSize: 13 }}>
                    Non-U.S. flag? Here&apos;s where to find your national safety-equipment requirements.
                </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
                {SAFETY_AGENCY_LINKS.map((link) => (
                    <a
                        key={link.country}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`safety-agency-link-${link.country.toLowerCase().replace(/\s+/g, "-")}`}
                        style={{
                            display: "block",
                            padding: "12px 14px",
                            border: "1px solid var(--bm-border-soft)",
                            borderRadius: 8,
                            textDecoration: "none",
                            color: "var(--bm-text)",
                            background: "var(--bm-bg-1)",
                            transition: "border-color 0.15s ease",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--bm-gold-dim)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--bm-border-soft)"; }}
                    >
                        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--bm-gold)", marginBottom: 4 }}>
                            {link.country}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {link.agency} <ExternalLink size={12} />
                        </div>
                        <div style={{ fontSize: 12, color: "var(--bm-text-muted)", lineHeight: 1.5 }}>
                            {link.description}
                        </div>
                    </a>
                ))}
            </div>
        </section>
    );
}

function defaultLabel(kind) {
    if (kind === "flares") return "Handheld flares";
    if (kind === "fire_extinguisher") return "Fire extinguisher";
    if (kind === "pfd") return "Life jackets";
    return "";
}

function kindLabel(kind) {
    if (kind === "flares") return "flare";
    if (kind === "fire_extinguisher") return "fire extinguisher";
    if (kind === "pfd") return "PFD";
    return "safety item";
}
